import { useErrorInlineNotification } from '@/components/ErrorInlineNotification';
import { PopupContainerProvider } from '@/components/ReportForm/Container/PopupContainer';
import { PictureInPictureModeProvider } from '@/context/InvoiceReports';
import type {
  AdminInvoiceReportColumn,
  AdminInvoiceReportsSearchQuery,
} from '@/features/admin_invoice_reports/types';
import {
  useBoundingClientRect,
  useWindowResize,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AdminInvoiceReportsListActions } from './AdminInvoiceReportsListActions';

import { ExportAdminInvoiceReports } from '../ExportAdminInvoiceReports';
import { BulkDeleteButtonGroup } from './BulkDeleteButtonGroup';
import { useBulkDeleteModal } from './ConfirmBulkDeleteModal/hooks/useBulkDeleteModal';
import { Search } from './Search';
import { AdminInvoiceReportsTable } from './Table';

import { useAdminInvoiceReportsFormInitial } from './hooks/useAdminInvoiceReportsFormInitial';
import { useAdminInvoiceReportsHandleSearchQuery } from './hooks/useAdminInvoiceReportsHandleSearchQuery';
import { useAdminInvoiceReportsSearchData } from './hooks/useAdminInvoiceReportsSearchData';
import { useExportAdminInvoiceReportsQuery } from './hooks/useExportAdminInvoiceReportsQuery';
import styles from './List.module.scss';
import { convertFormDataToApiQuery } from './Search/hooks/useSearchForm';

const cx = classnames.bind(styles);

// Stable empty constant — prevents new array reference on each render
const EMPTY_SELECTED: string[] = [];

const useTableSelection = (data: AdminInvoiceReportColumn[]) => {
  const [selectedRowKeys, setSelectedRowKeys] =
    useState<string[]>(EMPTY_SELECTED);

  const selectableItems = useMemo(
    () =>
      data.filter(
        (item) => item.delete_action.allowed && item.delete_action.visible
      ),
    [data]
  );

  const isAllSelected =
    selectableItems.length > 0 &&
    selectableItems.every((item) => selectedRowKeys.includes(item.id));

  const isAllDisabled = selectableItems.length === 0;

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedRowKeys(checked ? selectableItems.map((item) => item.id) : []);
    },
    [selectableItems]
  );

  const handleSelectRow = useCallback(
    (record: AdminInvoiceReportColumn, checked: boolean) => {
      setSelectedRowKeys((prev) =>
        checked ? [...prev, record.id] : prev.filter((id) => id !== record.id)
      );
    },
    []
  );

  return {
    selectedRowKeys,
    setSelectedRowKeys,
    isAllSelected,
    isAllDisabled,
    handleSelectAll,
    handleSelectRow,
  };
};

export const List: FC = memo(() => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { innerHeight } = useWindowResize();
  const { top } = useBoundingClientRect(containerRef);
  const containerStyle = useMemo(
    () => ({ height: top > 0 ? innerHeight - top : innerHeight }),
    [innerHeight, top]
  );

  const defaultValues = useAdminInvoiceReportsFormInitial();
  const initialQuery = useMemo(
    () => convertFormDataToApiQuery(defaultValues),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const { fetchArgs, searchQuery, sortOrders, onSearch, onMultiColumnSort } =
    useAdminInvoiceReportsHandleSearchQuery(initialQuery);

  const [
    { setError },
    ErrorContextHolder,
    ErrorInlineNotificationProvider,
    onClearMessage,
  ] = useErrorInlineNotification();

  const { data, pagination, refetch, isFetching } =
    useAdminInvoiceReportsSearchData(fetchArgs, setError);

  const { handleExport, isLoading: isExportLoading } =
    useExportAdminInvoiceReportsQuery(fetchArgs, { refetch });
  const isExportDisabled = useMemo(
    () => isFetching || data.length === 0,
    [isFetching, data.length]
  );

  const onHandleSearch = useCallback(
    (query: AdminInvoiceReportsSearchQuery) => {
      onClearMessage();
      onSearch(query, refetch);
    },
    [onSearch, refetch, onClearMessage]
  );

  const {
    selectedRowKeys,
    setSelectedRowKeys,
    isAllSelected,
    isAllDisabled,
    handleSelectAll,
    handleSelectRow,
  } = useTableSelection(data);

  // Clear selection when a new fetch starts
  const selectedCount = selectedRowKeys.length;

  useEffect(() => {
    if (isFetching && selectedCount > 0) {
      setSelectedRowKeys([]);
    }
  }, [isFetching, selectedCount, setSelectedRowKeys]);

  const isSearchEmpty = useMemo(
    () => data.length === 0 && Object.keys(fetchArgs.query ?? {}).length > 0,
    [data.length, fetchArgs.query]
  );

  const {
    bulkDeleteModalOpen,
    bulkDeleteCount,
    bulkDeleteIds,
    onOpenBulkDeleteModal,
    onCloseBulkDeleteModal,
  } = useBulkDeleteModal();

  /**
   * Extra margin under the list toolbar when the table does not show pagination controls.
   * Mirrors `invoice_reports` List spacing so the toolbar does not sit flush against the table / empty state.
   *
   * Edge cases (manual QA):
   * - **Single result page** (`maxPage === 1`): pagination UI is hidden → margin applies when not fetching.
   * - **No rows** (`data.length === 0`): same as above for empty / filtered-empty views.
   * - **While fetching** (`isFetching`): margin is off so height/spacing does not flicker from transient `maxPage` / row counts.
   * - **Multiple pages** (`maxPage > 1` with data): margin off; pagination provides layout separation.
   */
  const isApplyMbToClassName = useMemo(
    () => (pagination.maxPage === 1 || data.length === 0) && !isFetching,
    [pagination.maxPage, data.length, isFetching]
  );

  const handleDelete = useCallback(() => {
    onOpenBulkDeleteModal(selectedCount, selectedRowKeys);
  }, [onOpenBulkDeleteModal, selectedCount, selectedRowKeys]);

  return (
    <PictureInPictureModeProvider>
      <ErrorInlineNotificationProvider value={setError}>
        <div
          className={cx('container')}
          ref={containerRef}
          style={containerStyle}
        >
          <div className={cx('inlineNotificationWrap')}>
            {ErrorContextHolder}
          </div>
          <PopupContainerProvider>
            <Search
              defaultValues={defaultValues}
              onSearch={onHandleSearch}
              searchQuery={searchQuery}
              isLoading={isFetching}
            />
            <AdminInvoiceReportsListActions
              containerClassName={
                isApplyMbToClassName ? cx('header_mb') : undefined
              }
              leading={
                <BulkDeleteButtonGroup
                  selectedCount={selectedCount}
                  onBulkDelete={handleDelete}
                />
              }
              trailing={
                <ExportAdminInvoiceReports
                  isDisabled={isExportDisabled}
                  onExport={handleExport}
                  isLoading={isExportLoading}
                />
              }
            />
            <AdminInvoiceReportsTable
              data={data}
              pagination={pagination}
              searchQuery={searchQuery}
              isLoading={isFetching}
              isSearchEmpty={isSearchEmpty}
              sortOrders={sortOrders}
              onMultiColumnSort={onMultiColumnSort}
              onSearch={onSearch}
              selectedRowKeys={selectedRowKeys}
              handleSelectAll={handleSelectAll}
              handleSelectRow={handleSelectRow}
              isAllSelected={isAllSelected}
              isAllDisabled={isAllDisabled || isFetching}
              bulkDeleteModalOpen={bulkDeleteModalOpen}
              bulkDeleteCount={bulkDeleteCount}
              bulkDeleteIds={bulkDeleteIds}
              onCloseBulkDeleteModal={onCloseBulkDeleteModal}
              onListRefetch={refetch}
            />
          </PopupContainerProvider>
        </div>
      </ErrorInlineNotificationProvider>
    </PictureInPictureModeProvider>
  );
});

List.displayName = 'List';
